/**
 * @deprecated Trial is now credit-based, not time-based. Use hasAvailableCredits() instead.
 * Always returns false for backwards compatibility.
 */
export const isFreeTrialExpired = (isPlus, userCreatedAt) => {
  // Trial is no longer time-based - always return false
  // Free users are now limited by credits, not time
  return false;
};

/**
 * Check if user has available credits to send messages
 * @param {number} totalAvailableCredits - User's total available credits
 * @returns {boolean} - Whether user has credits available
 */
export const hasAvailableCredits = (totalAvailableCredits) => {
  return (totalAvailableCredits ?? 0) > 0;
};

/**
 * Offer verification when it unlocks a confirmed signup bonus or is required
 * to send. Legacy unverified accounts may already have received their full
 * grant, so an unverified email alone does not establish bonus eligibility.
 */
export function shouldOfferVerifyEmailForCredits(user) {
  if (!user?.id || !user.email) return false;
  if (user.flags?.isPlus || user.flags?.isGhost || user.flags?.isEmailVerified) return false;
  return user.flags?.isEmailVerificationRequired === true || getVerifyEmailRewardCredits(user) > 0;
}

/**
 * Only promise a reward confirmed by the server. A zero means no bonus is
 * owed; missing credit data uses the modal's generic verification copy until
 * the pending amount hydrates.
 */
export function getVerifyEmailRewardCredits(user) {
  const pending = Number(user?.credits?.pendingVerificationCredits);
  return Number.isFinite(pending) && pending > 0 ? pending : 0;
}

/** Open verification for a bonus or required gate; otherwise credit recovery. */
export function openCreditsRecoveryModal(openModal, user, options = {}) {
  if (typeof openModal !== 'function') return 'none';
  if (shouldOfferVerifyEmailForCredits(user)) {
    openModal('VerifyEmailModal', { source: options.source || 'credits_recovery' });
    return 'verify-email';
  }
  openModal('OutOfCreditsModal', options);
  return 'out-of-credits';
}

/**
 * Real-money credit packs remaining (excludes signup/earn/referral).
 * Used to unlock gated (`isPlusOnly`) models for non-Plus users.
 */
export const getPaidCreditsAvailable = (userOrCredits) => {
  if (typeof userOrCredits === 'number') return userOrCredits;
  const purchased = userOrCredits?.credits?.purchased || userOrCredits?.purchased || userOrCredits;
  return Number(purchased?.paidAvailable ?? 0) || 0;
};

export const hasPaidCredits = (userOrCredits) => getPaidCreditsAvailable(userOrCredits) > 0;

export const modelHasVideoOutput = (model) => !!model?.capabilities?.output?.hasVideo;

export const modelIsImageOnly = (model) => (
  !!model?.capabilities?.output?.hasImages && !modelHasVideoOutput(model)
);

/** Keep in sync with backend/utils/model_access.js FREEMIUM_AUTO_TEXT_FALLBACK_SLUGS. */
export const FREEMIUM_AUTO_TEXT_FALLBACK_SLUGS = [
  'kimi-k3',
  'arya',
  'deepseek-v4-flash',
  'minimax-m3',
  'glm-5-3',
];

/** Model needs Plus/paid unlock (or is image-only, which free users may use). */
export const modelNeedsAccessGate = (model) => (
  !!model?.flags?.isPlusOnly || modelHasVideoOutput(model)
);

/**
 * Whether the user may use this model.
 * - Plus: yes
 * - Image-only: yes for free users (Auto image gen / signup credits)
 * - Freemium Auto text defaults (coding/political fallbacks): signup credits OK
 * - Video and other gated models: need remaining real-money credit packs
 * - No model passed: true if Plus or paid credits (legacy blanket check)
 */
export const canAccessPlusOnlyModel = (user, model = null) => {
  if (!user) return false;
  if (user.flags?.isPlus) return true;
  if (model && modelIsImageOnly(model)) return true;
  if (model?.slug && FREEMIUM_AUTO_TEXT_FALLBACK_SLUGS.includes(String(model.slug))) return true;
  if (!model || modelNeedsAccessGate(model)) return hasPaidCredits(user);
  return true;
};

/** True when the UI should block selecting / starting this model. */
export const isModelLockedForUser = (user, model) => {
  if (!modelNeedsAccessGate(model)) return false;
  return !canAccessPlusOnlyModel(user, model);
};

/**
 * Open the free-user gated-model paywall (buy credits OR upgrade to Plus).
 * Plus users topping up should keep using CreditsPurchaseModal directly.
 */
export function openModelAccessPaywall(openModal, {
  model,
  title,
  subtitle,
  isVideo,
} = {}) {
  if (typeof openModal !== 'function') return;
  openModal('ModelAccessPaywallModal', {
    modelName: model?.name || null,
    isVideo: typeof isVideo === 'boolean' ? isVideo : modelHasVideoOutput(model),
    title,
    subtitle,
  });
}

/** True when an API/tool error is the Plus-or-paid-credits model gate. */
export function isPlusRequiredApiError(error) {
  if (!error) return false;
  const code = error?.data?.errorCode || error?.errorCode;
  if (code === 'plus_required') return true;
  const msg = String(error?.message || '');
  return /requires Gab AI Plus or purchased credits/i.test(msg)
    || /requires Plus or purchased credits/i.test(msg);
}

/**
 * Check if user is running low on credits
 * @param {number} totalAvailableCredits - User's total available credits
 * @param {number} threshold - Low credits threshold (default: 20)
 * @returns {boolean} - Whether user has low credits
 */
export const hasLowCredits = (totalAvailableCredits, threshold = 20) => {
  const credits = totalAvailableCredits ?? 0;
  return credits > 0 && credits <= threshold;
};

/**
 * Get credit status for display
 * @param {number} totalAvailableCredits - User's total available credits
 * @returns {'high' | 'medium' | 'low' | 'none'} - Credit status
 */
export const getCreditStatus = (totalAvailableCredits) => {
  const credits = totalAvailableCredits ?? 0;
  if (credits <= 0) return 'none';
  if (credits < 20) return 'low';
  if (credits < 100) return 'medium';
  return 'high';
};
